import { IntegrationLogo, integrationLabel } from '@clickguard/design-system'
import { WebsiteAvatar } from 'components'
import { Clock, FilterFunnel01, Flag01, Globe01 } from 'components/icons'
import { ReactNode } from 'react'
import { RuleEntities, useRuleEntities } from './rule-entities'
import { criterionParts } from './rule-summary'
import {
	ClickStatus,
	RuleActions,
	RuleDraft,
	RulePlatform,
	RuleScope,
	RuleTrigger
} from './types'

/** The WHERE / WHEN / AND / THEN readout, shared by the row's details drawer and the
 *  builder's summary strip, so the two cannot word a rule differently. */

const plural = (value: number, unit: string): string =>
	`${value} ${value === 1 ? unit.replace(/s$/, '') : unit}`

/** 14px, one step under `IntegrationLogo`'s 16px floor. Its box class is `cg-`
 *  prefixed and its sheet loads last, so only `!important` wins. */
const LOGO_SIZE = '!h-3.5 !w-3.5'

const STATUS_COLORS: Record<ClickStatus, string> = {
	Bad: 'text-[rgb(var(--cg-danger))]',
	Suspicious: 'text-[rgb(var(--cg-warning))]',
	Valid: 'text-[rgb(var(--cg-success))]'
}

/** A configured value: what the eye should land on, and what you'd go edit. */
const Em = ({ children, tone }: { children: ReactNode; tone?: string }) => (
	<span
		className={`font-medium ${tone ?? 'text-[rgb(var(--cg-text-primary))]'}`}
	>
		{children}
	</span>
)

const Lines = ({ parts }: { parts: ReactNode[] }) => (
	<>
		{parts.map((part, index) => (
			<div key={index}>{part}</div>
		))}
	</>
)

/** The open-ended parts of a clause — criteria, scope targets — in two columns. */
const Stacked = ({ items, dense }: { items: ReactNode[]; dense?: boolean }) => (
	<div
		className={`${
			// The strip is 460px wide, so a second column there would be two truncated ones.
			items.length > 4 && !dense ? 'sm:columns-2 sm:gap-x-8' : ''
		}`}
	>
		{items.map((item, index) => (
			<div key={index} className="break-inside-avoid">
				{item}
			</div>
		))}
	</div>
)

/** One mark per clause, never per line: colour stays spent on the click status. */
const CLAUSE_ICONS: Record<string, ReactNode> = {
	Where: <Globe01 />,
	When: <Clock />,
	And: <FilterFunnel01 />,
	Then: <Flag01 />
}

/** One ledger row: the clause name in the gutter, its statement beside it. */
const Row = ({ label, children }: { label: string; children: ReactNode }) => (
	<>
		<dt className="flex h-6 items-center gap-1.5 text-[11px] font-semibold uppercase leading-4 tracking-wide text-[rgb(var(--cg-text-secondary))]">
			<span aria-hidden className="shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5">
				{CLAUSE_ICONS[label]}
			</span>
			{label}
		</dt>
		<dd className="text-sm leading-6 text-[rgb(var(--cg-text-secondary))]">
			{children}
		</dd>
	</>
)

/** The asset the rule sits on, then the slice it was narrowed to. */
const whereClause = (
	entities: RuleEntities,
	scope: RuleScope,
	platform: RulePlatform
): { asset: ReactNode[]; targets: ReactNode } => {
	const asset: ReactNode[] = [
		<span className="inline-flex h-6 items-center gap-1.5 align-top">
			<WebsiteAvatar website={entities.website} size={3.5} />
			<Em>{entities.website.name}</Em>
		</span>
	]

	// An account or campaign list marks its own platform on every line; the website
	// scope keeps the standalone segment.
	if (scope.mode === 'website')
		asset.push(
			<span className="inline-flex h-6 items-center gap-1.5 align-top">
				<IntegrationLogo
					provider={platform}
					size={16}
					decorative
					className={LOGO_SIZE}
				/>
				<Em>{integrationLabel(platform)}</Em>
			</span>
		)

	return { asset, targets: scopeTargets(entities, scope) }
}

/** The scope target list: mark, label, values. The rows must share ONE grid —
 *  `max-content` sizes the label column to the widest label, and a grid per row
 *  would size each to itself. */
const TargetGrid = ({ children }: { children: ReactNode }) => (
	<div className="grid grid-cols-[auto_max-content_minmax(0,1fr)] gap-x-1.5">
		{children}
	</div>
)

/** One row of that grid. The three cells are siblings rather than a nested box,
 *  which would be invisible to the grid and take its own width. */
const TargetRow = ({
	mark,
	label,
	children
}: {
	mark: ReactNode
	label: ReactNode
	children: ReactNode
}) => (
	<>
		<span className="flex h-6 shrink-0 items-center">{mark}</span>
		{/* Padding on this cell, not a wider grid gap: the gap also sets mark-to-label. */}
		<span className="h-6 pr-3">{label}</span>
		<span className="min-w-0">{children}</span>
	</>
)

/** Target lists run long, so like the criteria they get a line each. */
/** The line a target gets when its id resolves to no name. It has to say SOMETHING:
 *  dropping the list silently would leave the reader with a DIFFERENT rule, one that
 *  reads as covering the whole site. The count is the rule's own. */
const UnnamedTargets = ({ count, noun }: { count: number; noun: string }) => (
	<div>
		<Em>{`${count} ${noun}${count === 1 ? '' : 's'}`}</Em>
	</div>
)

/** The tail of a partly-resolved list: a gap in the readout, not another target. */
const UnnamedRemainder = ({ count }: { count: number }) => (
	<div className="text-[rgb(var(--cg-text-secondary))]">+{count} more</div>
)

const scopeTargets = (entities: RuleEntities, scope: RuleScope): ReactNode => {
	if (scope.mode === 'accounts') {
		const selected = entities.adAccounts.filter((account) =>
			scope.values.includes(account.id)
		)
		const platforms = Array.from(
			new Set(selected.map((account) => account.platform))
		)
		const unnamed = scope.values.length - selected.length
		if (platforms.length === 0)
			return scope.values.length > 0 ? (
				<UnnamedTargets count={scope.values.length} noun="ad account" />
			) : null
		return (
			<>
				<TargetGrid>
					{platforms.map((platform: RulePlatform) => (
						<TargetRow
							key={platform}
							mark={
								<IntegrationLogo
									provider={platform}
									size={16}
									decorative
									className={LOGO_SIZE}
								/>
							}
							label={`${integrationLabel(platform)} accounts`}
						>
							{selected
								.filter((account) => account.platform === platform)
								.map((account) => (
									<div key={account.id}>
										<Em>{account.name}</Em>
									</div>
								))}
						</TargetRow>
					))}
				</TargetGrid>
				{unnamed > 0 && <UnnamedRemainder count={unnamed} />}
			</>
		)
	}

	if (scope.mode === 'campaigns') {
		const selected = entities.campaigns
			.filter((campaign) => scope.values.includes(campaign.id))
			.map((campaign) => ({
				...campaign,
				platform: entities.adAccounts.find(
					(account) => account.id === campaign.accountId
				)?.platform
			}))
		const platforms = Array.from(
			new Set(selected.map((campaign) => campaign.platform))
		).filter((platform): platform is RulePlatform => Boolean(platform))
		// A campaign whose ad account is missing has no platform, so it drops out of the
		// grid even though its name resolved — count against the rule's list.
		const named = selected.filter((campaign) => campaign.platform)
		const unnamed = scope.values.length - named.length
		if (platforms.length === 0)
			return scope.values.length > 0 ? (
				<UnnamedTargets count={scope.values.length} noun="campaign" />
			) : null
		return (
			<>
				<TargetGrid>
					{platforms.map((platform: RulePlatform) => (
						<TargetRow
							key={platform}
							mark={
								<IntegrationLogo
									provider={platform}
									size={16}
									decorative
									className={LOGO_SIZE}
								/>
							}
							label={`${integrationLabel(platform)} campaigns`}
						>
							{selected
								.filter((campaign) => campaign.platform === platform)
								.map((campaign) => (
									<div key={campaign.id}>
										<Em>{campaign.name}</Em>
									</div>
								))}
						</TargetRow>
					))}
				</TargetGrid>
				{unnamed > 0 && <UnnamedRemainder count={unnamed} />}
			</>
		)
	}

	return null
}

/** Names only what has been set. Nothing here is unconditional, so the clause can
 *  come back empty — the caller drops the WHEN row rather than print a bare label. */
const whenParts = (trigger: RuleTrigger): ReactNode[] => {
	const parts: ReactNode[] = []
	if (trigger.maxClicks !== '')
		parts.push(
			<>
				more than <Em>{plural(trigger.maxClicks, 'clicks')}</Em>
			</>
		)
	if (trigger.focus)
		parts.push(
			<>
				same <Em>{trigger.focus}</Em>
			</>
		)
	if (trigger.periodValue !== '')
		parts.push(
			<>
				within <Em>{plural(trigger.periodValue, trigger.periodUnit)}</Em>
			</>
		)
	return parts
}

/** Criteria are the one unbounded clause, so they never run inline. */
const andClause = (
	rule: RuleDraft
): { quantifier: ReactNode | null; criteria: ReactNode[] } => {
	const criteria = rule.criteria
		.map((selected): ReactNode => {
			const parts = criterionParts(selected)
			if (!parts) return null
			if (!parts.statement) return <Em>{parts.label}</Em>
			return (
				<>
					{parts.label} <Em>{parts.statement}</Em>
				</>
			)
		})
		.filter(Boolean)

	// An untyped percentage has no quantifier to state, so only this line drops.
	const quantifier =
		rule.threshold.mode === 'percentage' &&
		rule.threshold.percent === '' ? null : (
			<>
				<Em>
					{rule.threshold.mode === 'percentage'
						? `${rule.threshold.percent}%`
						: 'All'}
				</Em>{' '}
				of those clicks
			</>
		)

	return { quantifier, criteria }
}

/** Same rule as WHEN: the extras can be switched on first, so an unset status does
 *  not empty the clause. */
const thenParts = (actions: RuleActions): ReactNode[] => {
	const parts: ReactNode[] = []
	if (actions.clickStatus)
		parts.push(
			<>
				mark{' '}
				<Em tone={STATUS_COLORS[actions.clickStatus]}>{actions.clickStatus}</Em>
			</>
		)
	if (actions.addLabels && actions.labels.trim())
		parts.push(
			<>
				label <Em>{actions.labels.trim()}</Em>
			</>
		)
	if (actions.blacklist)
		parts.push(
			<>
				blacklist the source for{' '}
				<Em>{plural(actions.blacklistValue, actions.blacklistUnit)}</Em>
			</>
		)
	else {
		if (actions.tempIpExclusion)
			parts.push(<Em>exclude the IP on the targeted campaign</Em>)
		if (actions.audienceExclusion)
			parts.push(<Em>add the visitor to the excluded audience</Em>)
	}
	return parts
}

/** Only the platform lives outside the draft, so it comes in beside it. */
export type RuleLedgerSubject = RuleDraft & { platform: RulePlatform }

interface RuleLedgerProps {
	rule: RuleLedgerSubject
	/** Tighter gutter for the 460px builder strip. Typography is deliberately NOT
	 *  reduced: it is the same sentence the drawer prints. */
	dense?: boolean
}

export const RuleLedger = ({ rule, dense }: RuleLedgerProps) => {
	const entities = useRuleEntities()
	const where = whereClause(entities, rule.scope, rule.platform)
	const when = whenParts(rule.trigger)
	const and = andClause(rule)
	const then = thenParts(rule.actions)

	return (
		<dl
			className={`grid self-start ${
				dense
					? 'grid-cols-[56px_minmax(0,1fr)] gap-x-4 gap-y-1'
					: 'grid-cols-[84px_minmax(0,1fr)] gap-x-6 gap-y-2'
			}`}
		>
			<Row label="Where">
				<Lines parts={where.asset} />
				{/* Targets bring their own grid: the rows share one so the values line up. */}
				{where.targets}
			</Row>
			{/* A label with nothing after it reads as a clause that failed to render. */}
			{when.length > 0 && (
				<Row label="When">
					<Lines parts={when} />
				</Row>
			)}
			{and.criteria.length > 0 && (
				<Row label="And">
					{and.quantifier && <div>{and.quantifier}</div>}
					<Stacked items={and.criteria} dense={dense} />
				</Row>
			)}
			{then.length > 0 && (
				<Row label="Then">
					<Lines parts={then} />
					{rule.actions.blacklist && !dense && (
						<div className="mt-0.5 text-xs leading-5 text-[rgb(var(--cg-text-secondary))]">
							Blacklisting also excludes the IP on the targeted campaign and
							adds the visitor to the excluded audience.
						</div>
					)}
				</Row>
			)}
		</dl>
	)
}
